/**
 * ConversationPollLink.ts
 *
 * Utilitaire partagé pour lier un poll à sa conversation
 * Utilisé par PollCreator.tsx (sondages de dates) et ConversationProvider.tsx (formulaires)
 *
 * ARCHITECTURE CENTRÉE CONVERSATIONS (Session 1):
 * - Lien bidirectionnel Conversation ↔ Poll
 * - Conversation.pollId, pollType, pollStatus
 * - Poll.conversationId
 */

import {
  getConversation,
  updateConversation,
  createConversation,
  getConversations,
} from "./storage/ConversationStorageSimple";
import {
  getPollBySlugOrId,
  updatePollConversationLink,
  getPollByConversationId,
  type Poll,
} from "./pollStorage";
import { getCurrentUserId } from "./guestIdentityService";
import { logger } from "./logger";
import { ErrorFactory } from "./error-handling";
import type { Conversation } from "../types/conversation";
import i18n from "@core/i18n";

/**
 * Lie bidirectionnellement un poll à une conversation
 * Met à jour à la fois la conversation ET le poll
 *
 * @param conversationId - ID de la conversation
 * @param pollId - ID du poll
 * @param pollType - Type du poll ("date", "form", "availability" ou "quizz")
 */
export async function linkPollToConversationBidirectional(
  conversationId: string,
  pollId: string,
  pollType: "date" | "form" | "availability" | "quizz",
): Promise<void> {
  try {
    logger.info(
      "🔗 Liaison bidirectionnelle conversation ↔ poll",
      "conversation",
      {
        conversationId,
        pollId,
        pollType,
      },
    );

    // 1. Récupérer la conversation
    const conversation = getConversation(conversationId);
    if (!conversation) {
      throw ErrorFactory.storage(
        `Conversation not found: ${conversationId}`,
        i18n.t("conversationPoll.error.conversationNotFound"),
      );
    }

    // 2. Récupérer le poll (avec retry pour gérer le timing de cache)
    let poll = getPollBySlugOrId(pollId);
    let retries = 0;
    const maxRetries = 3;

    while (!poll && retries < maxRetries) {
      // Attendre un peu pour laisser le cache se mettre à jour
      await new Promise((resolve) => setTimeout(resolve, 100 * (retries + 1)));
      poll = getPollBySlugOrId(pollId);
      retries++;

      if (!poll && retries < maxRetries) {
        logger.warn(
          `⏳ Poll pas encore disponible, retry ${retries}/${maxRetries}...`,
          "conversation",
          {
            pollId,
          },
        );
      }
    }

    if (!poll) {
      throw ErrorFactory.storage(
        `Poll not found: ${pollId}`,
        i18n.t("conversationPoll.error.pollNotFound"),
      );
    }

    // 3. Vérifier si d'autres conversations sont liées à ce poll et les détacher
    const allConversations = getConversations();
    const otherLinkedConversations = allConversations.filter(
      (conv) =>
        conv.id !== conversationId &&
        (conv.pollId === pollId || conv.relatedPollId === pollId),
    );

    if (otherLinkedConversations.length > 0) {
      logger.warn(
        `⚠️ ${otherLinkedConversations.length} autre(s) conversation(s) déjà liée(s) à ce poll, détachement...`,
        "conversation",
        {
          pollId,
          existingConversations: otherLinkedConversations.map((c) => c.id),
        },
      );

      // Détacher les autres conversations
      otherLinkedConversations.forEach((conv) => {
        updateConversation({
          ...conv,
          pollId: undefined,
          pollType: undefined,
          pollStatus: undefined,
          relatedPollId: undefined,
          tags: conv.tags?.filter((tag) => tag !== `poll:${pollId}`) || [],
          metadata: conv.metadata
            ? {
                ...conv.metadata,
                pollGenerated: undefined,
                pollTitle: undefined,
                pollId: undefined,
              }
            : undefined,
        });
        logger.info("Conversation détachée du poll", "conversation", {
          conversationId: conv.id,
          pollId,
        });
      });
    }

    // 4. Mettre à jour la conversation avec les infos du poll
    updateConversation({
      ...conversation,
      pollId: poll.id,
      pollType: pollType,
      pollStatus: poll.status,
      metadata: {
        ...conversation.metadata,
        pollGenerated: true,
        pollTitle: poll.title,
      },
    });

    // 5. Mettre à jour le poll avec l'ID de la conversation
    updatePollConversationLink(poll.id, conversationId);

    logger.info("✅ Liaison bidirectionnelle réussie", "conversation", {
      conversationId,
      pollId,
    });
  } catch (error) {
    logger.error("❌ Erreur liaison bidirectionnelle", "conversation", {
      conversationId,
      pollId,
      error,
    });
    throw error;
  }
}

/**
 * Supprime le lien entre une conversation et son poll
 *
 * @param conversationId - ID de la conversation
 */
export function unlinkPollFromConversation(conversationId: string): void {
  try {
    const conversation = getConversation(conversationId);
    if (!conversation) {
      throw ErrorFactory.storage(
        `Conversation not found: ${conversationId}`,
        i18n.t("conversationPoll.error.conversationNotFound"),
      );
    }

    // Mettre à jour la conversation pour retirer le lien
    updateConversation({
      ...conversation,
      pollId: undefined,
      pollType: null,
      pollStatus: undefined,
      metadata: {
        ...conversation.metadata,
        pollGenerated: false,
        pollTitle: undefined,
      },
    });

    logger.info("✅ Lien poll supprimé de la conversation", "conversation", {
      conversationId,
    });
  } catch (error) {
    logger.error("❌ Erreur suppression lien poll", "conversation", {
      conversationId,
      error,
    });
    throw error;
  }
}

/**
 * Crée une conversation vide et lie un poll existant
 * Utilisé quand un poll est créé manuellement (sans IA)
 *
 * @param pollId - ID du poll
 * @param pollTitle - Titre du poll
 * @param pollType - Type du poll ("date", "form", "availability" ou "quizz")
 * @param isDraft - Si true, crée la conversation en mode brouillon (pour duplications)
 * @returns ID de la conversation créée ou existante
 */
export async function createConversationForPoll(
  pollId: string,
  pollTitle: string,
  pollType: "date" | "form" | "availability" | "quizz",
  isDraft: boolean = false,
): Promise<string> {
  try {
    logger.info(
      "🆕 Création conversation vide pour poll manuel",
      "conversation",
      {
        pollId,
        pollTitle,
        pollType,
      },
    );

    // Vérifier si une conversation existe déjà pour ce poll
    const existingConversations = getConversations();
    const existingConversation = existingConversations.find(
      (conv) => conv.pollId === pollId || conv.relatedPollId === pollId,
    );

    if (existingConversation) {
      logger.warn(
        "⚠️ Conversation existe déjà pour ce poll, retour de l'existante",
        "conversation",
        {
          conversationId: existingConversation.id,
          pollId,
        },
      );
      return existingConversation.id;
    }

    // Créer une conversation vide avec le userId approprié
    // En mode invité, utiliser "guest" pour être cohérent avec le filtrage du dashboard
    // En mode connecté, utiliser le userId authentifié
    const currentUserId = getCurrentUserId();
    // Si c'est un device ID (commence par "dev-"), on est en mode invité → utiliser "guest"
    const userId = currentUserId.startsWith("dev-") ? undefined : currentUserId;

    // Utiliser le titre du poll comme titre de conversation pour les sondages manuels
    // Le titre sera considéré comme "custom" et ne sera pas régénéré automatiquement
    const conversation = createConversation({
      title:
        pollTitle ||
        (pollType === "date"
          ? i18n.t("conversationPoll.title.date")
          : pollType === "availability"
            ? i18n.t("conversationPoll.title.availability")
            : pollType === "quizz"
              ? i18n.t("conversationPoll.title.quizz")
              : i18n.t("conversationPoll.title.form")),
      firstMessage:
        pollType === "date"
          ? i18n.t("conversationPoll.firstMessage.date")
          : pollType === "availability"
            ? i18n.t("conversationPoll.firstMessage.availability")
            : pollType === "quizz"
              ? i18n.t("conversationPoll.firstMessage.quizz")
              : i18n.t("conversationPoll.firstMessage.form"),
      userId: userId, // undefined sera converti en "guest" par createConversation
    });

    // Mettre à jour le status selon le contexte
    // - "active" pour les duplications (poll à modifier)
    // - "completed" pour les polls finalisés manuellement
    updateConversation({
      ...conversation,
      status: isDraft ? "active" : "completed",
    });

    // Lier le poll à la conversation avec retry (le poll peut ne pas être encore disponible)
    let linkSuccess = false;
    let retryCount = 0;
    const maxRetries = 3;

    while (!linkSuccess && retryCount < maxRetries) {
      try {
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * (retryCount + 1)),
        ); // Attendre 1s, 2s, 3s
        linkPollToConversationBidirectional(conversation.id, pollId, pollType);
        linkSuccess = true;
        logger.info("Conversation créée et liée au poll", "conversation", {
          conversationId: conversation.id,
          pollId,
          retryCount,
        });
      } catch (error) {
        retryCount++;
        logger.warn(
          `Tentative de liaison échouée ${retryCount}/${maxRetries}`,
          "conversation",
          {
            conversationId: conversation.id,
            pollId,
            error: error.message,
          },
        );

        if (retryCount >= maxRetries) {
          logger.error(
            "Échec de la liaison après tous les retries",
            "conversation",
            {
              conversationId: conversation.id,
              pollId,
              error: error.message,
            },
          );
          // Ne pas lancer d'erreur pour ne pas bloquer la duplication
        }
      }
    }

    // Déclencher un événement pour rafraîchir le Dashboard
    window.dispatchEvent(
      new CustomEvent("conversation-created", {
        detail: { conversationId: conversation.id },
      }),
    );

    // Invalider le cache TanStack Query pour forcer le rafraîchissement
    // Utiliser setTimeout pour éviter les problèmes de timing
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("invalidate-conversations"));
    }, 100);

    return conversation.id;
  } catch (error) {
    logger.error("❌ Erreur création conversation pour poll", "conversation", {
      pollId,
      error,
    });
    throw error;
  }
}

/**
 * Récupère une conversation avec son poll enrichi
 *
 * @param conversationId - ID de la conversation
 * @returns Objet contenant la conversation et le poll (si existe)
 */
export function getConversationWithPoll(conversationId: string): {
  conversation: Conversation;
  poll: Poll | null;
} | null {
  try {
    const conversation = getConversation(conversationId);
    if (!conversation) {
      return null;
    }

    // Récupérer le poll lié
    let poll: Poll | null = null;
    if (conversation.pollId) {
      poll = getPollBySlugOrId(conversation.pollId);
    } else {
      // Fallback: chercher par conversationId dans les polls
      poll = getPollByConversationId(conversationId);
    }

    return { conversation, poll };
  } catch (error) {
    logger.error("❌ Erreur récupération conversation + poll", "conversation", {
      conversationId,
      error,
    });
    return null;
  }
}
